import { type JSX, useState } from 'react'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'

import CashRegister from '@components/storeMap/floor/2d/CashRegister'
import DebugNeighbors from '@components/storeMap/floor/2d/DebugNeighbors'
import Shelf from '@components/storeMap/floor/2d/Shelf'
import ZoomHelper from '@components/storeMap/floor/2d/ZoomHelper'
import ShelfRegistry from '@components/storeMap/floor/ShelfRegistry'
import { useLayoutWithMapOrigin } from '@components/storeMap/utils'
import { useIsWideLayout } from '@hooks/useMediaQuery.ts'
import useShouldRotate from '@hooks/useShouldRotate.ts'
import useViewContextAwareHttpClient from '@utils/ViewContextAwareHttpClient'
import { cx } from '@utils/cx'

import type { TransformedShelf } from '@state/shelf/types'

export type ShelfProducer = (shelf: TransformedShelf) => JSX.Element
export type LayerProducer = (shelves: TransformedShelf[], width: number, height: number) => JSX.Element | null

function generatePaddingViewport(padding: number, layoutWidth: number, layoutHeight: number) {
  const linePadding = 0.2
  return `${-padding - linePadding} ${-padding - linePadding} ${layoutWidth + padding * 2 + linePadding * 2} ${layoutHeight + padding * 2 + linePadding * 2}`
}

interface FloorProps {
  initialScale?: number
  highlightedShelves: string[]
  onShelfClick?: (shelfId: string) => void
  shelfProducer?: ShelfProducer
  layerProducers?: LayerProducer[]
}

export type ShelfState = 'default' | 'highlighted' | 'selected' | 'oos' | 'highlightPercentage'

const defaultShelfProducer: ShelfProducer = (shelf) => <Shelf key={shelf.id} shelf={shelf} />

function Floor({ shelfProducer = defaultShelfProducer, layerProducers = [], initialScale }: FloorProps) {
  const { data: layout } = useLayoutWithMapOrigin()
  const [isDragging, setIsDragging] = useState(false)
  const { viewKey } = useViewContextAwareHttpClient()
  const shouldRotate = useShouldRotate()
  const isWideLayout = useIsWideLayout()

  const { width, height, shelves } = layout ?? { width: 0, height: 0, shelves: [] }
  const aspectRatio = height === 0 ? 0 : width / height

  let scale = isWideLayout ? 0.6 : 1
  if (initialScale) {
    scale = initialScale
  }

  // Don't render until layout data is available to prevent TransformWrapper
  // from initializing with 0-0 dimensions (which breaks resetZoom)
  if (!layout) return null

  const transformerClass = cx(
    'floor-map-transformer',
    !isWideLayout && 'size-full!',
    isWideLayout && 'max-h-full! w-full! max-w-full!',
    isDragging ? 'cursor-grabbing' : 'cursor-grab'
  )

  return (
    <TransformWrapper
      key={viewKey} // Force remount when the view changes to reset zoom state
      smooth={false} // Ironically, setting smooth to false makes the panning smoother
      zoomAnimation={{ disabled: true }} // disables map auto-centering when zooming in/out
      alignmentAnimation={{ disabled: true }} // disables map auto-centering when zooming in/out
      centerOnInit={true}
      minScale={0.65}
      maxScale={2.5}
      initialScale={scale}
      onZoomStop={(zoom) => {
        // Workaround for subpixel rendering blur after zooming.
        //
        // When react-zoom-pan-pinch applies CSS transforms (scale/translate),
        // the element can end up at non-integer pixel positions, causing the
        // browser to anti-alias text and lines, resulting in blurry SVG content.
        //
        // Toggling the margin triggers a browser reflow that can snap the element
        // back to whole pixel boundaries, restoring sharpness. The 'auto' value
        // doesn't visually move an already-centered element, making this imperceptible.
        const style = zoom.instance.wrapperComponent?.style
        if (!style) return

        style.margin = 'auto'

        const sharpnessTimer = setTimeout(() => {
          style.margin = '0'
        }, 2500)

        return () => clearTimeout(sharpnessTimer)
      }}
    >
      <TransformComponent
        wrapperClass="size-full!"
        contentClass={transformerClass}
        contentStyle={{ aspectRatio }}
        contentProps={{
          onMouseDown: () => setIsDragging(true),
          onMouseUp: () => setIsDragging(false),
        }}
      >
        <ShelfRegistry>
          <svg
            data-testid="floor:2d"
            xmlns="http://www.w3.org/2000/svg"
            viewBox={generatePaddingViewport(1, width, height)}
            className='size-full' // needed for iOS Safari
            preserveAspectRatio="xMidYMid meet"
            style={shouldRotate ? { transform: 'rotate(90deg)' } : undefined}
          >
            <rect
              x={-1}
              y={-1}
              width={width + 2}
              height={height + 2}
              fill="#ECECEC"
              stroke="#CDD2DB"
              strokeWidth={3}
              vectorEffect="non-scaling-stroke"
            />

            <g>
              {shelves.map(shelfProducer)}

              <CashRegister />
            </g>

            {layerProducers.map(layerProducer => layerProducer(shelves, width, height))}

            <DebugNeighbors />
          </svg>

          <ZoomHelper />
        </ShelfRegistry>
      </TransformComponent>
    </TransformWrapper>
  )
}

export default Floor